import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import _ from "lodash";
import jssVariables from "../../../jss_setup";
import { useContext } from "react";
import {
  CurrencyContext,
  StyleTypeContext,
  TranslationContext,
} from "../../../umbilical_tools";
import { useBookingProcess } from "../../../hooks/booking";
import Stepper from "../../common/Stepper";
import { Button } from "../../common/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  useFerriesGetSessionMeta,
  useGetFerQueryParams,
  useGetFerryDetails,
} from "../../../hooks/ferries";
import TravellersForms from "./TravellersForms";
import { PrebookUpdate } from "./PrebookUpdate";
import { useHistory } from "react-router-dom";
import { onViewFerryDetails } from "../../../pages/Ferries/Ferry";
import { Formik } from "formik";
import { validationSchema } from "./TravellersForms/helpers";
import { toast } from "react-toastify";
import { getInitialValues, initPayment, onBooking } from "./helpers";
import { useApoAuth, useBaseReservationPayload } from "../../../hooks";
import ExternallyHostedPayment from "../../common/StripeHostedPayment";
import { InlineIcon } from "@iconify/react";
import {
  ActionBtnsLoader,
  BackBtn,
  CustomerRemarks,
  SubmitGuestBtn,
} from "./components";

async function validateFn({ values, t, adults, seniors, required_fields }) {
  try {
    await validationSchema({
      t,
      adults,
      seniors,
      required_fields,
    }).validate(values, { abortEarly: false });
  } catch (error) {
    toast.warning(t("toastAlert"), {
      autoClose: 3000,
      closeOnClick: true,
      hideProgressBar: false,
      theme: "light",
      type: "warning",
      toastId: "correctAllFields",
      position: "top-center",
    });
  }
}

const styles = createUseStyles({
  FerriesBookingProcess: (props) => ({
    position: "absolute",
    zIndex: 9999,
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "white",
    display: "grid",
    ..._.get(props, "customStyles.FerriesBookingProcess", {}),
  }),
  FerriesBookingProcessContr: (props) => ({
    display: "grid",
    gridTemplateRows: props.noGapAfterBtns
      ? "max-content 1fr"
      : "max-content 1fr max-content",
    gridGap: jssVariables.normal_gap,
    padding: [
      jssVariables.double_gap,
      jssVariables.spacing.center_padding_mobile_medium[1],
    ],
    paddingBottom: `calc(${jssVariables.navbar_height} + ${jssVariables.normal_gap})`,
    minHeight: `calc(100vh - ${jssVariables.navbar_height})`,
    ..._.get(props, "customStyles.FerriesBookingProcessContr", {}),
  }),
  stepper: (props) => ({
    ..._.get(props, "customStyles.stepper", {}),
  }),
  travellersFormsContr: (props) => ({
    display: "grid",
    gridTemplateRows: "1fr max-content",
    ..._.get(props, "customStyles.travellersFormsContr", {}),
  }),
  content: (props) => ({
    width: "90vw",
    ..._.get(props, "customStyles.content", {}),
  }),
  actions: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridGap: jssVariables.normal_gap,
    paddingTop: jssVariables.normal_gap,
    ..._.get(props, "customStyles.actions", {}),
  }),
  loadingIconContr: (props) => ({
    display: "grid",
    placeItems: "center",
    boxShadow: jssVariables.shadows.normalShadow,
    borderRadius: jssVariables.half_gap,
    height: "30rem",
    ..._.get(props, "customStyles.loadingIconContr", {}),
  }),
  loadingIcon: (props) => ({
    fontSize: "xxx-large",
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.loadingIcon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    FerriesBookingProcess: (props) => ({
      ..._.get(props, "customStyles.FerriesBookingProcess.smallscreen", {}),
    }),
    FerriesBookingProcessContr: (props) => ({
      minWidth: "45rem",
      maxWidth: "60rem",
      justifySelf: "center",
      height: "100vh",
      paddingBottom: jssVariables.double_gap,
      ..._.get(
        props,
        "customStyles.FerriesBookingProcessContr.smallscreen",
        {}
      ),
    }),
    stepper: (props) => ({
      ..._.get(props, "customStyles.stepper.smallscreen", {}),
    }),
    content: (props) => ({
      width: "100%",
      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
    actions: (props) => ({
      ..._.get(props, "customStyles.actions.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    FerriesBookingProcess: (props) => ({
      ..._.get(props, "customStyles.FerriesBookingProcess.bigscreen", {}),
    }),
    FerriesBookingProcessContr: (props) => ({
      maxWidth: "60rem",
      ..._.get(props, "customStyles.FerriesBookingProcessContr.bigscreen", {}),
    }),
    stepper: (props) => ({
      ..._.get(props, "customStyles.stepper.bigscreen", {}),
    }),
    content: (props) => ({
      ..._.get(props, "customStyles.content.bigscreen", {}),
    }),
    actions: (props) => ({
      ..._.get(props, "customStyles.actions.bigscreen", {}),
    }),
  },
});
const FerriesBookingProcess = ({ page_session_id }) => {
  const [loadingData, setLoadingData] = useState(false);

  const [travellers, setTravellers] = useState(null);
  const [bookRemarks, setBookRemarks] = useState("");
  const [prebookUpdateData, setPrebookUpdateData] = useState(null);
  const [agreed, setAgreed] = useState(false);

  const { source_entity } = useApoAuth();

  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { t } = useContext(TranslationContext);
  const history = useHistory();

  const { selectionData } = useGetFerQueryParams();
  const required_fields = selectionData.required_fields || [];

  const { step, setStep, onIncreaseStep, onDecreaseStep } = useBookingProcess();

  const dispatch = useDispatch();
  const { currency: settedCurrency } = useContext(CurrencyContext);

  const classes = styles({
    customStyles: cStyler({
      type,
      brand,
      objName: "FerriesBookingProcess",
    }),
    noGapAfterBtns: step === 0,
  });

  const { search_session_id, loadingMeta } = useFerriesGetSessionMeta({
    page_session_id,
  });
  useGetFerryDetails({
    search_session_id,
    provider: selectionData.provider,
    token: selectionData.token,
  });

  const { loadingFerSelected, adults, children, seniors } = useSelector(
    (state) => ({
      searchPayload: state.FerrySearchForm.searchPayload,
      adults: state.FerrySearchForm?.adults || 0,
      children: state.FerrySearchForm?.children || 0,
      seniors: state.FerrySearchForm?.seniors || 0,
      loadingFerSelected: state.FerrySelected.loading,
    })
  );

  const loading = loadingFerSelected || loadingData || loadingMeta;

  const leader_info = useBaseReservationPayload({
    group_leader: travellers?.adults.find((traveller) => traveller.is_leader),
  });

  function prevStep() {
    if (step > 0) {
      onDecreaseStep();
      return;
    }
    onViewFerryDetails({
      history,
      provider: selectionData.provider,
      token: selectionData.token,
      page_session_id,
    });
  }

  async function nextStep(travellersData) {
    if (step === 0) {
      setTravellers(travellersData);
      onIncreaseStep();
    } else if (step === 1) {
      onIncreaseStep();
    } else if (step === 2) {
      if (!agreed) {
        toast.warning(t("pleaseCheckTheBox"));
        return;
      }
      onIncreaseStep();
      setLoadingData(true);
      const response = await onBooking({
        payload: {
          session_id: search_session_id,
          booking_id: prebookUpdateData.booking_id,
          booking_token: prebookUpdateData.booking_token,
          customer_remarks: bookRemarks,
          source_entity,
          customer_user_data: leader_info,
          website_meta: {
            api_key: prebookUpdateData.api_uid,
            name: prebookUpdateData.provider,
          },
        },
      });

      if (!response) {
        history.push(`/failed-booking/${page_session_id}`);
        return;
      }

      const initialized = await initPayment({
        payload: {
          customer_user_data: leader_info,
          page_session_id,
          settedCurrency,
          source_entity,
          amount: _.get(response, "booking_result.payment_data.amount"),
          currency: _.get(response, "booking_result.payment_data.currency"),
          reference: _.get(response, "booking_result.reference"),
        },
        dispatch,
      });

      if (!initialized) {
        history.push(`/failed-booking/${page_session_id}`);
        return;
      }
    } else {
      return;
    }
  }

  const initialValues = getInitialValues({
    adults,
    children,
    seniors,
    required_fields,
  });

  const backBtnProps = {
    step,
    t,
    prevStep,
  };

  return (
    <div className={classes.FerriesBookingProcess}>
      <div className={classes.FerriesBookingProcessContr}>
        <div className={classes.stepper}>
          <Stepper
            steps={[t("travelInfo"), t("remarks"), t("prebook"), t("payment")]}
            current={step === 4 ? 2 : step}
          />
        </div>
        {step === 0 ? (
          loading ? (
            <React.Fragment>
              <div className={classes.loadingIconContr}>
                <InlineIcon
                  icon="eos-icons:loading"
                  className={classes.loadingIcon}
                />
              </div>
              <div className={classes.actions}>
                <ActionBtnsLoader />
              </div>
            </React.Fragment>
          ) : (
            <Formik
              validateOnChange={false}
              validateOnBlur={false}
              initialValues={initialValues}
              validationSchema={validationSchema({
                t,
                adults,
                seniors,
                required_fields,
              })}
              validate={(values) =>
                validateFn({ values, t, adults, seniors, required_fields })
              }
              onSubmit={(values) => nextStep(values)}>
              <div className={classes.travellersFormsContr}>
                <div className={classes.content}>
                  <TravellersForms required_fields={required_fields} />
                </div>
                <div className={classes.actions}>
                  <BackBtn backBtnProps={backBtnProps} />
                  <SubmitGuestBtn id="ferries_next_btn" />
                </div>
              </div>
            </Formik>
          )
        ) : (
          <React.Fragment>
            <div className={classes.content}>
              <React.Fragment>
                {step === 1 && (
                  <CustomerRemarks
                    id="ferries_remarks"
                    bookRemarks={bookRemarks}
                    setBookRemarks={setBookRemarks}
                  />
                )}
                {step === 2 && (
                  <PrebookUpdate
                    travellers={travellers}
                    selectionData={selectionData}
                    loadingData={loadingData}
                    setLoadingData={setLoadingData}
                    prebookUpdateData={prebookUpdateData}
                    setPrebookUpdateData={setPrebookUpdateData}
                    setStep={setStep}
                    agreed={agreed}
                    setAgreed={setAgreed}
                  />
                )}
                {step === 3 && <ExternallyHostedPayment />}
              </React.Fragment>
            </div>
            <div className={classes.actions}>
              {loading ? (
                <ActionBtnsLoader />
              ) : (
                <React.Fragment>
                  <BackBtn backBtnProps={backBtnProps} />
                  <Button
                    id="ferries_next_btn"
                    text={step < 2 ? t("next") : t("payment")}
                    bold={true}
                    size="large"
                    onClick={nextStep}
                  />
                </React.Fragment>
              )}
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};
FerriesBookingProcess.defaultProps = {
  page_session_id: "",
};
FerriesBookingProcess.propTypes = {
  page_session_id: PropTypes.string,
};
export default FerriesBookingProcess;
